import React, { useState, useEffect, useCallback } from 'react';
import { Star, Plus, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { useAuth } from '../../hooks/useAuth';
import { SOP } from '../../types/sop';
import favoritesService from '@/services/favoritesService';
import { toast } from '../ui/use-toast';
import { Skeleton } from '../ui/skeleton';
import { useCategories } from '@/context/CategoryContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

interface AddToFavoritesDialogProps {
  sop: SOP;
}

export const AddToFavoritesDialog: React.FC<AddToFavoritesDialogProps> = ({ sop }) => {
  const { user, displayName, profilePictureUrl } = useAuth();
  const { categories, refreshCategories } = useCategories();
  const [isOpen, setIsOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isUnfavoriteDialogOpen, setIsUnfavoriteDialogOpen] = useState(false);

  const checkIfFavorited = useCallback(() => {
    const isInAnyCategory = categories.some(category =>
      category.sopIds.includes(sop.id)
    );
    setIsFavorited(isInAnyCategory);
  }, [categories, sop.id]);

  useEffect(() => {
    checkIfFavorited();
  }, [checkIfFavorited]);

  const handleCreateCategory = async () => {
    if (!user?.uid || !newCategoryName.trim()) return;

    try {
      setIsLoading(true);
      
      // Use the display name from AuthContext
      const newCategory = await favoritesService.createCategory(
        newCategoryName,
        user.uid,
        displayName || 'Unknown User',
        profilePictureUrl || undefined
      );

      await favoritesService.addSOPToCategory(sop.id, newCategory.id, sop);
      await refreshCategories();

      setNewCategoryName('');
      setIsCreatingCategory(false);
      setIsOpen(false);
      setIsFavorited(true);

      toast({
        title: "Success",
        description: `Added to new category "${newCategoryName}"`,
      });
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: "Error",
        description: "Failed to create category",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCategory = async (categoryId: string) => {
    if (!user?.uid) return;

    try {
      setIsLoading(true);
      await favoritesService.addSOPToCategory(sop.id, categoryId, sop);
      await refreshCategories();

      setIsOpen(false);
      setIsFavorited(true);

      toast({
        title: "Success",
        description: "Added to favorites",
      });
    } catch (error) {
      console.error('Error adding to category:', error);
      toast({
        title: "Error",
        description: "Failed to add to favorites",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnfavorite = async () => {
    if (!user?.uid) return;

    try {
      setIsLoading(true);
      await Promise.all(categories.map(category => 
        favoritesService.removeSOPFromCategory(sop.id, category.id)
      ));
      await refreshCategories();

      setIsFavorited(false);
      setIsUnfavoriteDialogOpen(false);

      toast({
        title: "Success",
        description: "Removed from favorites",
      });
    } catch (error) {
      console.error('Error removing from favorites:', error);
      toast({
        title: "Error",
        description: "Failed to remove from favorites",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFavorited && !isLoading) {
    return (
      <>
        <Button 
          variant="ghost" 
          size="sm" 
          className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400"
          onClick={() => setIsUnfavoriteDialogOpen(true)}
        >
          <Check className="h-4 w-4" />
          <span className="hidden md:inline">Favorited</span>
        </Button>

        <AlertDialog open={isUnfavoriteDialogOpen} onOpenChange={setIsUnfavoriteDialogOpen}>
          <AlertDialogContent aria-describedby="unfavorite-description" className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-gray-900 dark:text-white">Remove from Favorites</AlertDialogTitle>
              <AlertDialogDescription id="unfavorite-description" className="text-gray-600 dark:text-gray-400">
                Are you sure you want to remove this SOP from your favorites?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleUnfavorite} className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800">
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400">
          <Star className="h-4 w-4" />
          <span className="hidden md:inline">Add to Favorites</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white dark:bg-gray-900" aria-describedby="favorites-description">
        <DialogHeader>
          <DialogTitle className="text-gray-900 dark:text-white">Add to Favorites</DialogTitle>
          <DialogDescription id="favorites-description" className="text-gray-600 dark:text-gray-400">
            Select a category or create a new one to add this SOP to your favorites.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full dark:bg-gray-700" />
              <Skeleton className="h-10 w-full dark:bg-gray-700" />
              <Skeleton className="h-10 w-full dark:bg-gray-700" />
            </div>
          ) : (
            <>
              {/* Existing Categories */}
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleAddToCategory(category.id)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center justify-between text-gray-900 dark:text-gray-100"
                  >
                    <span>{category.name}</span>
                    <Star className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  </button>
                ))}
              </div>

              {/* Create New Category */}
              {isCreatingCategory ? (
                <div className="space-y-2">
                  <div id="new-category-description" className="sr-only">Enter a name for your new category</div>
                  <Input
                    placeholder="Category Name"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreateCategory();
                      }
                    }}
                    className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    aria-describedby="new-category-description"
                  />
                  <div className="flex gap-2">
                    <Button onClick={handleCreateCategory} className="flex-1 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white">
                      Create
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsCreatingCategory(false)}
                      className="flex-1 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setIsCreatingCategory(true)}
                  className="w-full flex items-center justify-center gap-2 bg-green-600 dark:bg-green-700 border-green-600 dark:border-green-700 text-white hover:bg-green-700 dark:hover:bg-green-800"
                >
                  <Plus className="h-4 w-4" />
                  New Category
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddToFavoritesDialog;
